import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import AddCircleOutlined from '@mui/icons-material/AddCircleOutlined';
import Edit from '@mui/icons-material/Edit';
import FiberManualRecord from '@mui/icons-material/FiberManualRecord';
import RemoveCircleOutlined from '@mui/icons-material/RemoveCircleOutlined';
import { FieldChange } from '../types';
import { ChangeDescriptionParts, generateChangeDescriptionParts } from '../utils';

interface NaturalLanguageViewProps {
  changes: FieldChange[];
  userName: string;
}

const CHANGE_ICON: Record<string, React.ReactNode> = {
  added: <AddCircleOutlined fontSize='small' sx={{ color: 'success.main' }} />,
  removed: <RemoveCircleOutlined fontSize='small' sx={{ color: 'error.main' }} />,
  modified: <Edit fontSize='small' sx={{ color: 'warning.main' }} />,
};

const changeIcon = (changeType: FieldChange['changeType']): React.ReactNode =>
  CHANGE_ICON[changeType] ?? <FiberManualRecord fontSize='small' sx={{ color: 'text.disabled' }} />;

const CHANGE_PALETTE = {
  added: 'success',
  removed: 'error',
  modified: 'warning',
} as const satisfies Record<FieldChange['changeType'], 'success' | 'error' | 'warning'>;

/** An old or new value, shown inline as monospace so it stands out from the sentence. */
const Value: React.FC<{ tone: 'old' | 'new'; children: React.ReactNode }> = ({ tone, children }) => (
  <Box
    component='span'
    sx={(theme) => ({
      display: 'inline-block',
      px: 0.75,
      py: 0.25,
      borderRadius: 0.5,
      fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, monospace',
      fontSize: '0.875em',
      fontWeight: 500,
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      verticalAlign: 'middle',
      bgcolor: alpha(theme.palette[tone === 'old' ? 'error' : 'success'].main, 0.15),
      color: tone === 'old' ? 'error.main' : 'success.main',
      ...(tone === 'old' && { textDecoration: 'line-through' }),
    })}
  >
    {children}
  </Box>
);

const UserName: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component='strong' sx={{ fontWeight: 600 }}>
    {children}
  </Box>
);

const FieldName: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component='em' sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
    {children}
  </Box>
);

export const NaturalLanguageView: React.FC<NaturalLanguageViewProps> = ({ changes, userName }) => {
  if (changes.length === 0) {
    return <Typography sx={{ color: 'text.secondary' }}>No changes detected</Typography>;
  }

  const renderChangeDescription = (parts: ChangeDescriptionParts) => {
    const { changeType } = parts;

    if (changeType === 'modified') {
      return (
        <>
          <UserName>{parts.userName}</UserName> {parts.action} <FieldName>{parts.fieldName}</FieldName> from{' '}
          <Value tone='old'>{parts.oldValue}</Value> to <Value tone='new'>{parts.newValue}</Value>
        </>
      );
    }

    if (changeType === 'added') {
      return (
        <>
          <UserName>{parts.userName}</UserName> {parts.action} <Value tone='new'>{parts.newValue}</Value>
          {parts.suffix} <FieldName>{parts.fieldName}</FieldName>
        </>
      );
    }

    if (changeType === 'removed') {
      return (
        <>
          <UserName>{parts.userName}</UserName> {parts.action} <FieldName>{parts.fieldName}</FieldName>
          {parts.suffix} <Value tone='old'>{parts.oldValue}</Value>
          {parts.suffix === ' (was' && ')'}
        </>
      );
    }

    return (
      <>
        <UserName>{parts.userName}</UserName> {parts.action} <FieldName>{parts.fieldName}</FieldName>
      </>
    );
  };

  return (
    <Box component='ul' sx={{ listStyle: 'none', p: 0, m: 0 }}>
      {changes.map((change, index) => (
        <Stack
          component='li'
          key={`${change.fieldName}-${index}`}
          direction='row'
          spacing={1}
          sx={{
            alignItems: 'baseline',
            py: 1,
            px: 1,
            bgcolor: (theme) => alpha(theme.palette[CHANGE_PALETTE[change.changeType]].main, 0.06),
            borderBottom: 1,
            borderColor: 'divider',
            '&:last-of-type': { borderBottom: 'none' },
          }}
        >
          <Box sx={{ display: 'flex', alignSelf: 'center' }}>{changeIcon(change.changeType)}</Box>
          <Box sx={{ flex: 1, lineHeight: 1.5, minWidth: 0 }}>
            {renderChangeDescription(generateChangeDescriptionParts(change, userName))}
          </Box>
        </Stack>
      ))}
    </Box>
  );
};
